<template>
  <v-container id="signature_profiles_root" fluid>
    <!-- DIALOG -->
    <router-view name="dialog" @uploaded="searchSignatureProfiles"></router-view>
    <!-- CONTENT -->
    <v-flex>
      <v-row class="mb-6 px-6" align="center" justify="center">
        <v-col cols="7">
          <span class="text-h5">
            {{ $t('signatureProfiles.title.list') }}
          </span>
        </v-col>

        <v-col class="d-flex flex-row">
          <v-btn id="create_profile" color="primary" class="align-self-end mr-8" to="/signatureProfiles/add">
            <v-icon left>mdi-plus</v-icon>
            {{ $t('signatureProfiles.content.createProfile') }}
          </v-btn>

          <v-text-field
            id="search_field"
            v-model="search"
            :label="$t('dataTable.search')"
            append-icon="mdi-magnify"
            single-line
            hide-details
            clearable
          ></v-text-field>
        </v-col>
      </v-row>

      <v-card class="pa-2">
        <v-data-table
          must-sort
          :search="search"
          :headers="headers"
          :items="signatureProfileList"
          :options.sync="pagination"
          :server-items-length="totalItems"
          :loading="dataTableLoading"
          :loading-text="$t('dataTable.content.loadingPleaseWait')"
          :no-data-text="$t('dataTable.content.noDataAvailable')"
          :footer-props="{
            'items-per-page-text': $t('dataTable.footer.itemsPerPageText'),
            'page-text': $t('dataTable.footer.pageText'),
            'items-per-page-options': [10,25,50],
          }"
          calculate-widths
        >
          <template #[`item.name`]="{item}">
            <long-text :max-char="40" :text="item.name" />
          </template>
          <template #[`item.actions`]="{ item,index }">
            <v-row justify="end">
              <!-- preview -->
              <v-tooltip
                color="blue-grey lighten-3"
                left
                :open-on-click="false"
                :open-on-hover="true"
                :open-on-focus="true"
              >
                <template #activator="{ on, attrs}">
                  <v-btn
                    text
                    class="prev-btn elevation-0 pa-1"
                    v-bind="attrs"
                    v-on="on"
                    @mouseover=" getImageDataUrl(item,index)"
                    @mouseleave="previewImageUrl=null"
                  >
                    <v-icon>mdi-image-outline</v-icon>
                    <span class="text-none">{{ $t('dataTable.content.action.preview') }}</span>
                  </v-btn>
                </template>
                <v-container class="py-0 d-flex flex-column">
                  <v-img v-if="!!previewImageUrl" contain max-height="100" max-width="200" :src="previewImageUrl"></v-img>
                  <span

                    class="font-size-15 black--text text-center"
                  >
                    {{ getText(item) || "" }}
                  </span>
                </v-container>
              </v-tooltip>

              <!-- edit -->

              <v-btn text class="update-btn elevation-0 text--darken-2 pa-1" :to="'signatureProfiles/' + item.id + '/update'">
                <v-icon>mdi-progress-pencil</v-icon>
                <span>{{ $t('dataTable.content.action.update') }}</span>
              </v-btn>

              <!-- delete -->

              <v-btn text class="elevation-0 text--darken-2 pa-1" @click="deleteSignatureProfile(item.id, $event)">
                <v-icon>mdi-delete</v-icon>
                <span>{{ $t('dataTable.content.action.delete') }}</span>
              </v-btn>
            </v-row>
          </template>
        </v-data-table>
      </v-card>
    </v-flex>
  </v-container>
</template>

<script>
import LongText from '@/components/LongText.vue'
import debounce from 'lodash.debounce'
import { showConfirmationMenu, showToastError, showToastSuccess } from '../../../event-bus'
import signatureProfiles from '../../../services/api/signatureProfiles'

export default {
  name: 'SignatureProfiles',
  components: {
    LongText
  },
  data() {
    return {
      search: null,
      pagination: {
        sortby: ['name']
      },
      signatureProfileList: [],
      totalItems: 0,
      dataTableLoading: null,
      headers: [
        {
          text: this.$t('dataTable.header.signatureProfiles.name'),
          align: 'left',
          value: 'name'
        },

        {
          text: this.$t('dataTable.header.actions'),
          align: 'right',
          value: 'actions',
          sortable: false
        }
      ],
      previewImageUrl: ''
    }
  },

  watch: {
    search() {
      this.dataTableLoading = true
      this.searchSignatureProfilesWatcher()
    },
    pagination: {
      handler() {
        this.searchSignatureProfiles()
      }
    }
  },

  methods: {
    async searchSignatureProfiles() {
      this.dataTableLoading = true

      try {
        const pageRequest = {
          'page-num': this.pagination.page,
          'page-size': this.pagination.itemsPerPage || 10,
          'sort-field': this.pagination.sortBy[0],
          'sort-order': this.pagination.sortDesc[0] ? 'desc' : 'asc'
        }
        const page = await signatureProfiles.listMySignatureProfiles(this.search, pageRequest)
        this.signatureProfileList = page.content
        this.totalItems = page.total
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.dataTableLoading = false
      }
    },

    searchSignatureProfilesWatcher: debounce(function() {
      this.searchSignatureProfiles()
    }, 500),

    async deleteSignatureProfile(id, e) {
      const conf = await showConfirmationMenu({
        title: this.$t('dialog.confirmation.title'),
        posX: e.clientX,
        posY: e.clientY
      })
      if (!conf) {
        return
      }

      try {
        await signatureProfiles.deleteMySignatureProfile(id)
        this.searchSignatureProfiles()
        showToastSuccess(this.$t('toastMessage.succesfullyDeleted'))
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },
    getText(item) {
      return item.textParams ? item.textParams.text : ''
    },
    async getImageDataUrl(item, idx) {
      if (!Object.prototype.hasOwnProperty.call(item, 'sourceImageDataUrl')) {
        const profile = await signatureProfiles.getMySignatureProfile(item.id)
        item.sourceImageDataUrl = profile.sourceImageDataUrl

        this.$set(this.signatureProfileList, idx, profile)
        this.$set(this.signatureProfileList[idx], 'sourceImageDataUrl', profile.sourceImageDataUrl)
      }

      this.previewImageUrl = item.sourceImageDataUrl
    }
  }
}
</script>

<style scoped>
.v-btn::before {
  background: transparent;
}
</style>
